import { useEffect, useState, useRef, useCallback } from "react";

import { Avatar } from "@bigbinary/neeto-atoms";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@bigbinary/neeto-atoms/primitives";
import { CircleHelp } from "lucide-react";
import { isPresent } from "neetocist";
import useHotkeys from "neetohotkeys";
import { Profile } from "neetoteam";
import { not } from "ramda";
import KeyboardShortcuts from "v2/components/KeyboardShortcuts";

import SubscriptionUpgradeRequestModal from "components/SubscriptionUpgradeRequestModal";

import ProductSwitcherModal from "./ProductSwitcher/Modal";
import ProfileMenu from "./ProfileMenu";

import { DEFAULT_MENU_LINK_PROPS } from "../constants";
import { getDefaultBottomLinks, getProfileInfo } from "../utils";

const ProfileButton = ({
  profileInfoOverrides,
  showProductSwitcher,
  bottomLinks,
  topLinks,
  customContent,
  isThemeSwitcherEnabled,
  isOrganizationSwitcherEnabled,
  profilePopoverProps,
  isConsumer,
  enableSubscriptionUpgradeRequest,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [changesCount, setChangesCount] = useState(null);
  const timeoutRef = useRef(null);

  const [, setIsOpen] = KeyboardShortcuts.usePaneState();

  useHotkeys(["ctrl+u", "command+u"], () => setIsModalOpen(not), {
    mode: "global",
  });

  const profileInfo = getProfileInfo(profileInfoOverrides, isConsumer);

  useEffect(() => {
    const Nv = {
      onReceiveData: data => {
        if (!data.unreadCount) return;
        setChangesCount(data.unreadCount);
      },
      onShow: () => {
        setChangesCount(null);
      },
    };

    window.Nv = window.Nv ? Object.assign(window.Nv, Nv) : Nv;
  }, []);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsPopoverOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setIsPopoverOpen(false), 150);
  }, []);

  const newBottomLinks = [
    ...getDefaultBottomLinks(profileInfo.isAuthenticated, isConsumer),
    ...bottomLinks,
  ];

  const defaultLinks = Object.keys(DEFAULT_MENU_LINK_PROPS)
    .map(key => ({
      ...DEFAULT_MENU_LINK_PROPS[key],
      ...(key === "keyboardShortcutProps" && {
        onClick: () => setIsOpen(prev => !prev),
      }),
      ...(key === "engageProps" && changesCount && { changesCount }),
    }))
    .filter(({ hide }) => !hide);

  return (
    <>
      <Profile.Pane popoverProps={profilePopoverProps} />
      {isPopoverOpen && (
        <div className="pointer-events-none fixed inset-0 z-[99998] bg-black/[0.27]" />
      )}
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverAnchor asChild>
          <button
            className="bg-primary flex flex-col items-center justify-center gap-1.5 rounded-e-2xl p-1"
            type="button"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Avatar
              className="size-[1.625rem] shrink-0 rounded-full border-2 border-white"
              data-testid="profile-avatar"
              user={{ name: profileInfo.name, imageUrl: profileInfo.imageUrl }}
            />
            <span className="flex size-[1.625rem] items-center justify-center rounded-full bg-white">
              <CircleHelp size={20} />
            </span>
          </button>
        </PopoverAnchor>
        <PopoverContent
          align="end"
          className="z-[99999] max-h-[var(--radix-popover-content-available-height)] w-64 overflow-y-auto rounded-xl p-0"
          collisionPadding={16}
          side="right"
          sideOffset={8}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ProfileMenu
            {...{
              customContent,
              defaultLinks,
              enableSubscriptionUpgradeRequest,
              isConsumer,
              isOrganizationSwitcherEnabled,
              isThemeSwitcherEnabled,
              profileInfo,
              showProductSwitcher,
              topLinks,
            }}
            bottomLinks={newBottomLinks}
            openUpgradeModal={() => setIsUpgradeModalOpen(true)}
            toggleModal={setIsModalOpen}
          />
        </PopoverContent>
      </Popover>
      {isPresent(changesCount) && (
        <span
          className="bg-accent-foreground pointer-events-none absolute end-0 top-0 z-[3] flex h-[0.9375rem] min-w-[0.9375rem] items-center justify-center rounded-full border border-white px-1 text-[10px] text-white select-none"
          data-testid="help-icon-changes-count"
        >
          {changesCount}
        </span>
      )}
      <ProductSwitcherModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      {enableSubscriptionUpgradeRequest && (
        <SubscriptionUpgradeRequestModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
        />
      )}
    </>
  );
};

export default ProfileButton;
